import {
  BTAttendanceRecord,
  BTAttendanceUpdatePayload,
  BTClub,
  BTLocation,
} from '../../services/api/btApi';

export type BTAttendanceEducationType = 'new' | 'renewal' | 'advanced';

export interface BTAttendanceEditFormData {
  name: string;
  phone: string;
  churchName: string;
  locationId: string;
  clubId: string;
  educationType: BTAttendanceEducationType;
}

export type BTAttendanceEditFieldErrors = Partial<Record<keyof BTAttendanceEditFormData, string>>;

const LOCATION_ALIAS_GROUPS = [
  ['경상지역_양산 평산교회', '경상지역_양산평산교회', '경상지역_평산교회'],
];

const normalizeIdentity = (value?: string) => (value || '')
  .normalize('NFKC')
  .trim()
  .replace(/\s+/g, '')
  .toUpperCase();

const isSameLocation = (left?: string, right?: string) => {
  const normalizedLeft = normalizeIdentity(left);
  const normalizedRight = normalizeIdentity(right);
  if (!normalizedLeft || !normalizedRight) return false;
  if (normalizedLeft === normalizedRight) return true;

  return LOCATION_ALIAS_GROUPS.some((group) => {
    const normalizedGroup = group.map(normalizeIdentity);
    return normalizedGroup.includes(normalizedLeft) && normalizedGroup.includes(normalizedRight);
  });
};

const getClubLabel = (club: BTClub) => club.bt_club_name || club.club_name || '';

const inferEducationType = (record: BTAttendanceRecord): BTAttendanceEducationType => {
  if (record.education_type) return record.education_type;
  if (record.session?.basicTrainingLevel) return record.session.basicTrainingLevel;

  switch (record.session?.educationProgram) {
    case 'bt-renewal':
      return 'renewal';
    case 'bt-advanced':
      return 'advanced';
    default:
      return 'new';
  }
};

export const getAttendanceLocationLabel = (record: BTAttendanceRecord) => (
  record.bt_location_name
  || record.location
  || record.session?.location
  || '-'
);

export const createBTAttendanceEditForm = (
  record: BTAttendanceRecord,
  locations: BTLocation[],
  clubs: BTClub[],
  fallbackLocationId = '',
): BTAttendanceEditFormData => {
  const teacher = typeof record.teacherId === 'object' ? record.teacherId : undefined;
  const recordLocationName = getAttendanceLocationLabel(record);
  const matchingLocation = locations.find((location) => (
    isSameLocation(location.location_name, recordLocationName)
  ));
  const fallbackLocation = locations.find((location) => location._id === fallbackLocationId);

  const recordClubName = record.bt_club_name
    || record.session?.btClubTrack
    || record.session?.btClubTracks?.[0]
    || '';
  const matchingClub = clubs.find((club) => (
    normalizeIdentity(getClubLabel(club)) === normalizeIdentity(recordClubName)
  ));

  return {
    name: record.name || teacher?.teacherName || '',
    phone: record.teacher_phone || teacher?.teacherPhone || '',
    churchName: record.church_name || teacher?.churchName || '',
    locationId: record.bt_location_id || matchingLocation?._id || fallbackLocation?._id || '',
    clubId: record.bt_club_id || matchingClub?._id || '',
    educationType: inferEducationType(record),
  };
};

export const validateBTAttendanceEditForm = (
  form: BTAttendanceEditFormData,
  isLeadership: boolean,
): BTAttendanceEditFieldErrors => {
  const errors: BTAttendanceEditFieldErrors = {};
  if (!form.name.trim()) errors.name = '교사명을 입력해 주세요.';

  const phoneDigits = form.phone.replace(/\D/g, '');
  if (!form.phone.trim()) {
    errors.phone = '연락처를 입력해 주세요.';
  } else if (!/^[\d\s-]+$/.test(form.phone) || !/^\d{10,11}$/.test(phoneDigits)) {
    errors.phone = '연락처는 숫자 10~11자리와 하이픈만 입력해 주세요.';
  }

  if (!form.churchName.trim()) errors.churchName = '교회명을 입력해 주세요.';
  if (!isLeadership) {
    if (!form.locationId) errors.locationId = '수강지역을 선택해 주세요.';
    if (!form.educationType) errors.educationType = '교육형태를 선택해 주세요.';
  }
  return errors;
};

export const buildBTAttendanceUpdatePayload = (
  initialForm: BTAttendanceEditFormData,
  currentForm: BTAttendanceEditFormData,
  isLeadership: boolean,
): BTAttendanceUpdatePayload => {
  const payload: BTAttendanceUpdatePayload = {};
  const initialName = initialForm.name.trim();
  const currentName = currentForm.name.trim();
  if (initialName !== currentName) payload.name = currentName;

  const initialPhone = initialForm.phone.trim();
  const currentPhone = currentForm.phone.trim();
  if (initialPhone !== currentPhone) payload.teacher_phone = currentPhone;

  const initialChurchName = initialForm.churchName.trim();
  const currentChurchName = currentForm.churchName.trim();
  if (initialChurchName !== currentChurchName) payload.church_name = currentChurchName;

  if (!isLeadership) {
    if (initialForm.locationId !== currentForm.locationId) {
      payload.bt_location_id = currentForm.locationId;
    }
    if (currentForm.clubId && initialForm.clubId !== currentForm.clubId) {
      payload.bt_club_id = currentForm.clubId;
    }
    if (initialForm.educationType !== currentForm.educationType) {
      payload.education_type = currentForm.educationType;
    }
  }

  return payload;
};
